/** The receipt as a chain: which links hold, which do not apply, and which are missing.
 *
 * `docs/design/36-user-journeys.md` section 5 asks for a vertical chain "so the unbroken trail is
 * visible". An unbroken trail is only visible if a broken one is: a link that is not there has to
 * appear as a link that is not there, with the reason, and not as a row that quietly is not
 * rendered. This module is the decision about each link; `ReceiptChain.tsx` draws it.
 *
 * **Nothing here is a second opinion.** The server's evidence assessment (`evidence_status.py`,
 * six links: referenced, stored, retrievable, verified, priced, ledgered) is the one judgement of
 * whether a figure is traceable, and it arrives on the receipt as `evidence_met`,
 * `evidence_not_applicable`, `evidence_broken_at` and `evidence_why_not`. A node's standing is
 * derived from those and from the data the node itself shows. Two homes for "is this figure
 * traceable" is how a badge and a chain come to disagree, so a link nobody checked is reported as
 * **not established** rather than as met — which is the repair F10 made on the server, kept here.
 *
 * Pure, so the standing of every link is tested without rendering a drawer.
 */

import type { Receipt } from "../api/queries";

/** The server's six links, in the order it assesses them. */
export const LINK_STEPS = [
  "referenced",
  "stored",
  "retrievable",
  "verified",
  "priced",
  "ledgered",
] as const;
export type LinkStep = (typeof LINK_STEPS)[number];

/** Three, and none may be collapsed into another: *not applicable* is not a step that failed. */
export type Standing = "present" | "not_applicable" | "missing";

export type NodeKey =
  | "source"
  | "activity"
  | "factor"
  | "method"
  | "coverage"
  | "calculation"
  | "verifier"
  | "ledger";

export interface ChainNode<K extends string = NodeKey> {
  key: K;
  label: string;
  standing: Standing;
  /** Why the link is missing or does not apply. Null for a link that holds. */
  reason: string | null;
  /** The server's evidence links this node carries, so a reader can see which check it answers. */
  links: readonly LinkStep[];
}

/** One link's standing, from the server's assessment and nothing else.
 *
 * `is_complete` is the same assessment's verdict: it is true only when every link is met or not
 * applicable (`Assessment.is_complete`). So a receipt that is complete and lists no links - a
 * response that predates the lists - has no missing link, and drawing one would contradict the
 * badge above it. A receipt that is *not* complete and lists nothing reads as nothing established.
 */
export function linkStanding(receipt: Receipt, step: LinkStep): Standing {
  const met: readonly string[] | undefined = receipt.evidence_met;
  const na: readonly string[] | undefined = receipt.evidence_not_applicable;
  if (met?.includes(step)) return "present";
  if (na?.includes(step)) return "not_applicable";
  return receipt.is_complete ? "present" : "missing";
}

const worst = (standings: readonly Standing[]): Standing =>
  standings.includes("missing")
    ? "missing"
    : standings.length > 0 && standings.every((s) => s === "not_applicable")
      ? "not_applicable"
      : "present";

/** The label of the node a server link belongs to, for "the first break is at …". */
const NODE_OF: Record<LinkStep, string> = {
  referenced: "Source document",
  stored: "Source document",
  retrievable: "Source document",
  verified: "Verifier",
  priced: "Emission factor",
  ledgered: "Ledger entry",
};

/** Why a link is missing, in one sentence.
 *
 * The server states the **first** break only (`evidence_why_not`), because the later links are
 * unreachable anyway and a reader given four problems fixes none of them. So the first broken link
 * gets that sentence, and any later missing link says it is not established and points at the break
 * that comes first. The sentence is never rewritten here: it is the server's.
 */
function whyMissing(receipt: Receipt, links: readonly LinkStep[]): string {
  const brokenAt = receipt.evidence_broken_at as LinkStep | null | undefined;
  if (brokenAt && links.includes(brokenAt) && receipt.evidence_why_not) return receipt.evidence_why_not;
  if (brokenAt) {
    return `Not established. The chain breaks earlier, at ${NODE_OF[brokenAt].toLowerCase()}.`;
  }
  return receipt.evidence_why_not || "Not established for this figure.";
}

export function buildChain(receipt: Receipt): ChainNode[] {
  const nodes: ChainNode[] = [];

  // --- 1. The source document (referenced, stored, retrievable) ---------------------------------
  const sourceLinks: readonly LinkStep[] = ["referenced", "stored", "retrievable"];
  const statedBasis = receipt.reference_kind === "stated_basis";
  const dangling = Boolean(receipt.source_document_id) && !receipt.source_document;
  const sourceStanding = worst(sourceLinks.map((s) => linkStanding(receipt, s)));
  let source: ChainNode;
  if (dangling) {
    // An id that resolves to nothing: the link names a document and does not reach one.
    source = {
      key: "source",
      label: "Source document",
      standing: "missing",
      reason: "The document this figure cites could not be found.",
      links: sourceLinks,
    };
  } else if (sourceStanding === "missing") {
    source = {
      key: "source",
      label: "Source document",
      standing: "missing",
      reason: whyMissing(receipt, sourceLinks),
      links: sourceLinks,
    };
  } else if (statedBasis) {
    // ADR-0036: a figure on a person's attestation has no document, and rendering it as though
    // one were missing would send somebody looking for a file nobody ever claimed existed.
    source = {
      key: "source",
      label: "Source document",
      standing: "not_applicable",
      reason:
        "No document: this figure rests on a stated basis a person attested, so there is nothing to store or retrieve.",
      links: sourceLinks,
    };
  } else {
    source = { key: "source", label: "Source document", standing: sourceStanding, reason: null, links: sourceLinks };
  }
  nodes.push(source);

  // --- 2. The activity value --------------------------------------------------------------------
  nodes.push({ key: "activity", label: "Activity value", standing: "present", reason: null, links: [] });

  // --- 3. The locked factor (priced) ------------------------------------------------------------
  const priced = linkStanding(receipt, "priced");
  if (priced === "missing") {
    nodes.push({
      key: "factor",
      label: "Emission factor",
      standing: "missing",
      reason: whyMissing(receipt, ["priced"]),
      links: ["priced"],
    });
  } else if (receipt.factor.value === null) {
    // Rule 3's honesty for a figure not priced from a published rate: it says so.
    nodes.push({
      key: "factor",
      label: "Emission factor",
      standing: "not_applicable",
      reason: "No emission factor was applied to this figure. The calculation states how it was reached.",
      links: ["priced"],
    });
  } else {
    nodes.push({ key: "factor", label: "Emission factor", standing: "present", reason: null, links: ["priced"] });
  }

  // --- 4. Method and GWP set --------------------------------------------------------------------
  nodes.push(
    receipt.factor.gwp_set_label
      ? { key: "method", label: "Method and GWP set", standing: "present", reason: null, links: [] }
      : {
          key: "method",
          label: "Method and GWP set",
          standing: "not_applicable",
          reason: "No GWP set is cited for this figure.",
          links: [],
        },
  );

  // --- (instrument coverage, only where a figure was priced against one) ------------------------
  if (receipt.covered_by.length > 0) {
    nodes.push({ key: "coverage", label: "Covered by", standing: "present", reason: null, links: [] });
  }

  // --- 5. The calculation: the arithmetic, once --------------------------------------------------
  nodes.push({ key: "calculation", label: "Calculation", standing: "present", reason: null, links: [] });

  // --- 6. The verifier ---------------------------------------------------------------------------
  const verified = linkStanding(receipt, "verified");
  nodes.push({
    key: "verifier",
    label: "Verifier",
    standing: verified,
    reason:
      verified === "missing"
        ? whyMissing(receipt, ["verified"])
        : verified === "not_applicable"
          ? "Verification does not apply to this figure."
          : null,
    links: ["verified"],
  });

  // --- 7. The ledger entry -----------------------------------------------------------------------
  // Two things have to agree: the entry exists (the data this node prints) and the server's
  // `ledgered` link holds. An entry shown beside a link the server did not establish lowers the
  // claim rather than raising it - the same direction as F10's repair.
  const ledgered = linkStanding(receipt, "ledgered");
  const recorded = receipt.ledger.length > 0;
  nodes.push({
    key: "ledger",
    label: "Ledger entry",
    standing: recorded && ledgered !== "missing" ? "present" : "missing",
    reason: !recorded
      ? "No entry — this figure is not yet recorded."
      : ledgered === "missing"
        ? whyMissing(receipt, ["ledgered"])
        : null,
    links: ["ledgered"],
  });

  return nodes;
}

export interface ChainSummary {
  present: number;
  not_applicable: number;
  missing: number;
  total: number;
}

export function summarise(nodes: readonly ChainNode<string>[]): ChainSummary {
  return {
    present: nodes.filter((n) => n.standing === "present").length,
    not_applicable: nodes.filter((n) => n.standing === "not_applicable").length,
    missing: nodes.filter((n) => n.standing === "missing").length,
    total: nodes.length,
  };
}
